import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { clientsApi } from '../../services/clients';
import { casesApi } from '../../services/cases';
import { tasksApi } from '../../services/tasks';
import { dashboardApi, OverdueInvoice } from '../../services/dashboard';
import { invoicesApi } from '../../services/invoices';
import { timeApi, CreateTimeEntryRequest } from '../../services/time';
import { documentsApi, PendingDocument } from '../../services/documents';
import { Case } from '../../types';
import { TimeEntryModal } from '../../components/TimeEntryModal';
import {
  DashboardTaskWithCase,
  sortTasksByDueDateAndPriority,
  filterIncompleteTasks,
  getPriorityColor,
} from './dashboardTaskUtils';
import { formatLocalDate } from '../../utils/date';

export function AdvocateDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalClients: 0,
    activeClients: 0,
    prospectClients: 0,
    totalCases: 0,
    activeCases: 0,
    tasksDueToday: 0,
    tasksOverdue: 0,
    tasksPending: 0,
  });
  const [recentTasks, setRecentTasks] = useState<DashboardTaskWithCase[]>([]);
  const [overdueInvoices, setOverdueInvoices] = useState<OverdueInvoice[]>([]);
  const [overdueLoading, setOverdueLoading] = useState(true);
  const [pendingDocuments, setPendingDocuments] = useState<PendingDocument[]>([]);
  const [pendingDocsLoading, setPendingDocsLoading] = useState(true);
  const [sendingReminder, setSendingReminder] = useState<string | null>(null);
  const [completingTask, setCompletingTask] = useState<DashboardTaskWithCase | null>(null);

  useEffect(() => {
    loadStats();
    loadOverdueInvoices();
    loadPendingDocuments();
  }, []);

  const loadOverdueInvoices = async () => {
    try {
      const data = await dashboardApi.getOverdueInvoices();
      setOverdueInvoices(data.invoices);
    } catch (err) {
      console.error('Failed to load overdue invoices:', err);
    } finally {
      setOverdueLoading(false);
    }
  };

  const loadPendingDocuments = async () => {
    try {
      const data = await documentsApi.getPendingDocuments();
      setPendingDocuments(data.documents);
    } catch (err) {
      console.error('Failed to load pending documents:', err);
    } finally {
      setPendingDocsLoading(false);
    }
  };

  const handleSendReminder = async (invoiceId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (sendingReminder) return;
    
    setSendingReminder(invoiceId);
    try {
      await invoicesApi.remind(invoiceId);
      alert('Reminder sent successfully!');
    } catch (err) {
      console.error('Failed to send reminder:', err);
      alert('Failed to send reminder. Please try again.');
    } finally {
      setSendingReminder(null);
    }
  };

  const handleApplyPayment = (invoiceId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/invoices/${invoiceId}?action=payment`);
  };

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  };

  const formatDaysOverdue = (days: number) => {
    if (days === 0) return 'Due today';
    if (days > 0) return `${days} day${days === 1 ? '' : 's'} overdue`;
    return `Due in ${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'}`;
  };

  /**
   * Handle task completion with time entry.
   * Creates a time entry and marks the task as completed.
   * Requirements: 4.2, 4.3
   */
  const handleTimeEntrySubmit = async (timeEntry: CreateTimeEntryRequest) => {
    if (!completingTask) return;
    
    try {
      // Create time entry with the task's caseId
      await timeApi.create(timeEntry);
      // Mark task as completed
      await tasksApi.update(completingTask.taskId, { status: 'completed' });
      // Remove completed task from display
      setRecentTasks(prev => prev.filter(t => t.taskId !== completingTask.taskId));
      // Update stats
      setStats(prev => ({
        ...prev,
        tasksPending: Math.max(0, prev.tasksPending - 1),
      }));
    } catch (err) {
      console.error('Failed to complete task:', err);
      alert('Failed to complete task. Please try again.');
    } finally {
      setCompletingTask(null);
    }
  };

  /**
   * Handle task completion without time entry (skip).
   * Marks the task as completed without creating a time entry.
   * Requirements: 4.4, 4.5
   */
  const handleTimeEntrySkip = async () => {
    if (!completingTask) return;
    
    try {
      // Mark task as completed without time entry
      await tasksApi.update(completingTask.taskId, { status: 'completed' });
      // Remove completed task from display
      setRecentTasks(prev => prev.filter(t => t.taskId !== completingTask.taskId));
      // Update stats
      setStats(prev => ({
        ...prev,
        tasksPending: Math.max(0, prev.tasksPending - 1),
      }));
    } catch (err) {
      console.error('Failed to complete task:', err);
      alert('Failed to complete task. Please try again.');
    } finally {
      setCompletingTask(null);
    }
  };

  /**
   * Handle cancel of task completion.
   * Closes the modal without making any API calls, leaving the task unchanged.
   * Requirements: 1.2, 1.3, 3.1
   */
  const handleTimeEntryCancel = () => {
    setCompletingTask(null);
  };

  const loadStats = async () => {
    try {
      const [clientsData, casesData] = await Promise.all([
        clientsApi.list(),
        casesApi.list(),
      ]);
      
      // Create a map of caseId to case for quick lookup
      const caseMap = new Map<string, Case>();
      casesData.cases.forEach(c => caseMap.set(c.caseId, c));
      
      // Load tasks from all cases and create TaskWithCase objects
      const allTasksWithCase: DashboardTaskWithCase[] = [];
      for (const c of casesData.cases) {
        try {
          const taskData = await tasksApi.list(c.caseId);
          // Join task data with case data to get studentName
          const tasksWithCase = taskData.tasks.map(task => ({
            ...task,
            studentName: `${c.childFirstName} ${c.childLastName}`,
          }));
          allTasksWithCase.push(...tasksWithCase);
        } catch (e) {
          // Skip if no tasks
        }
      }
      
      const today = new Date().toISOString().split('T')[0];
      const pendingTasks = allTasksWithCase.filter(t => t.status !== 'completed');
      const tasksDueToday = pendingTasks.filter(t => t.dueDate === today).length;
      const tasksOverdue = pendingTasks.filter(t => t.dueDate && t.dueDate < today).length;
      
      // Filter to incomplete tasks, sort by due date then priority, take top 5
      const incompleteTasks = filterIncompleteTasks(allTasksWithCase);
      const sortedTasks = sortTasksByDueDateAndPriority(incompleteTasks);
      const upcomingTasks = sortedTasks.slice(0, 5);
      
      setRecentTasks(upcomingTasks);
      setStats({
        totalClients: clientsData.count,
        activeClients: clientsData.clients.filter(c => c.status === 'active').length,
        prospectClients: clientsData.clients.filter(c => c.status === 'prospect').length,
        totalCases: casesData.count,
        activeCases: casesData.cases.filter(c => c.status === 'active').length,
        tasksDueToday,
        tasksOverdue,
        tasksPending: pendingTasks.length,
      });
    } catch (err) {
      console.error('Failed to load stats:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ margin: 0 }}>Advocate Dashboard</h1>
          <p style={{ color: '#666', marginTop: '8px' }}>Welcome back! Here's your practice overview.</p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={() => navigate('/clients/new')}
            style={{ backgroundColor: '#1976d2', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '4px', cursor: 'pointer' }}
          >
            + New Client
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
        <DashboardCard
          title="Total Clients"
          value={loading ? '...' : stats.totalClients.toString()}
          color="#1a237e"
          link="/clients"
          linkText="View All"
        />
        <DashboardCard
          title="Active Clients"
          value={loading ? '...' : stats.activeClients.toString()}
          color="#4caf50"
          link="/clients?status=active"
          linkText="View Active"
        />
        <DashboardCard
          title="Prospects"
          value={loading ? '...' : stats.prospectClients.toString()}
          color="#2196f3"
          link="/clients?status=prospect"
          linkText="View Prospects"
        />
        <DashboardCard
          title="Active Cases"
          value={loading ? '...' : stats.activeCases.toString()}
          color="#ff9800"
        />
        <DashboardCard
          title="Pending Documents"
          value={pendingDocsLoading ? '...' : pendingDocuments.length.toString()}
          color={pendingDocuments.length > 0 ? '#ff9800' : '#4caf50'}
          link="/documents/pending"
          linkText="View Queue"
        />
      </div>

      <div style={{ marginTop: '32px' }}>
        <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h2 style={{ margin: 0, fontSize: '18px' }}>Upcoming Tasks</h2>
            <span
              onClick={() => navigate('/tasks')}
              style={{ color: '#1976d2', fontSize: '14px', cursor: 'pointer' }}
            >
              View All →
            </span>
          </div>
          {/* Task Summary Counts */}
          <div style={{ display: 'flex', gap: '24px', marginBottom: '20px', paddingBottom: '16px', borderBottom: '1px solid #e0e0e0' }}>
            <div>
              <div style={{ fontSize: '28px', fontWeight: 'bold', color: stats.tasksOverdue > 0 ? '#f44336' : '#4caf50' }}>{loading ? '...' : stats.tasksOverdue}</div>
              <div style={{ fontSize: '12px', color: '#666' }}>Overdue</div>
            </div>
            <div>
              <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#ff9800' }}>{loading ? '...' : stats.tasksDueToday}</div>
              <div style={{ fontSize: '12px', color: '#666' }}>Due Today</div>
            </div>
            <div>
              <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#1976d2' }}>{loading ? '...' : stats.tasksPending}</div>
              <div style={{ fontSize: '12px', color: '#666' }}>Pending</div>
            </div>
          </div>
          {recentTasks.length === 0 ? (
            <p style={{ color: '#666' }}>No upcoming tasks.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {recentTasks.map(t => {
                const today = new Date().toISOString().split('T')[0];
                const isOverdue = t.dueDate && t.dueDate < today;
                return (
                  <div
                    key={t.taskId}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '10px 12px',
                      backgroundColor: '#f5f5f5',
                      borderRadius: '4px',
                      gap: '12px',
                    }}
                  >
                    {/* Completion Checkbox */}
                    <input
                      type="checkbox"
                      checked={false}
                      onChange={() => setCompletingTask(t)}
                      onClick={(e) => e.stopPropagation()}
                      style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                      title="Mark as complete"
                    />
                    {/* Priority Pill */}
                    <span
                      style={{
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: 500,
                        backgroundColor: getPriorityColor(t.priority),
                        color: 'white',
                        textTransform: 'capitalize',
                        minWidth: '50px',
                        textAlign: 'center',
                      }}
                    >
                      {t.priority}
                    </span>
                    {/* Student Name and Task Title */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '14px', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {t.title}
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {t.studentName}
                      </div>
                    </div>
                    {/* Due Date */}
                    <span
                      style={{
                        fontSize: '12px',
                        color: isOverdue ? '#f44336' : '#666',
                        fontWeight: isOverdue ? 500 : 400,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {t.dueDate ? formatLocalDate(t.dueDate) : 'No due date'}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Overdue Invoices Section */}
      <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginTop: '24px' }}>
        <h2 style={{ marginBottom: '16px', fontSize: '18px' }}>Invoices Due Soon / Overdue</h2>
        {overdueLoading ? (
          <p style={{ color: '#666' }}>Loading...</p>
        ) : overdueInvoices.length === 0 ? (
          <p style={{ color: '#666' }}>No invoices due soon or overdue.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
                <th style={{ textAlign: 'left', padding: '12px 8px', fontSize: '14px', color: '#666' }}>Invoice</th>
                <th style={{ textAlign: 'left', padding: '12px 8px', fontSize: '14px', color: '#666' }}>Client</th>
                <th style={{ textAlign: 'left', padding: '12px 8px', fontSize: '14px', color: '#666' }}>Student</th>
                <th style={{ textAlign: 'left', padding: '12px 8px', fontSize: '14px', color: '#666' }}>Due Date</th>
                <th style={{ textAlign: 'left', padding: '12px 8px', fontSize: '14px', color: '#666' }}>Status</th>
                <th style={{ textAlign: 'right', padding: '12px 8px', fontSize: '14px', color: '#666' }}>Balance</th>
                <th style={{ textAlign: 'right', padding: '12px 8px', fontSize: '14px', color: '#666' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {overdueInvoices.map(inv => (
                <tr
                  key={inv.invoiceId}
                  onClick={() => navigate(`/invoices/${inv.invoiceId}`)}
                  style={{ borderBottom: '1px solid #e0e0e0', cursor: 'pointer' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <td style={{ padding: '12px 8px', fontSize: '14px' }}>{inv.invoiceNumber}</td>
                  <td style={{ padding: '12px 8px', fontSize: '14px' }}>{inv.clientName}</td>
                  <td style={{ padding: '12px 8px', fontSize: '14px' }}>{inv.studentName}</td>
                  <td style={{ padding: '12px 8px', fontSize: '14px' }}>{formatLocalDate(inv.dueDate)}</td>
                  <td style={{ padding: '12px 8px', fontSize: '14px' }}>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: 500,
                      backgroundColor: inv.daysOverdue > 0 ? '#ffebee' : '#fff3e0',
                      color: inv.daysOverdue > 0 ? '#c62828' : '#e65100',
                    }}>
                      {formatDaysOverdue(inv.daysOverdue)}
                    </span>
                  </td>
                  <td style={{ padding: '12px 8px', fontSize: '14px', textAlign: 'right', fontWeight: 500 }}>
                    {formatCurrency(inv.balanceDue)}
                  </td>
                  <td style={{ padding: '12px 8px', textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                      {inv.clientEmail && (
                        <button
                          onClick={(e) => handleSendReminder(inv.invoiceId, e)}
                          disabled={sendingReminder === inv.invoiceId}
                          style={{
                            backgroundColor: '#fff',
                            color: '#1976d2',
                            border: '1px solid #1976d2',
                            padding: '6px 12px',
                            borderRadius: '4px',
                            cursor: sendingReminder === inv.invoiceId ? 'not-allowed' : 'pointer',
                            fontSize: '12px',
                            opacity: sendingReminder === inv.invoiceId ? 0.6 : 1,
                          }}
                        >
                          {sendingReminder === inv.invoiceId ? 'Sending...' : 'Send Reminder'}
                        </button>
                      )}
                      <button
                        onClick={(e) => handleApplyPayment(inv.invoiceId, e)}
                        style={{
                          backgroundColor: '#4caf50',
                          color: 'white',
                          border: 'none',
                          padding: '6px 12px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px',
                        }}
                      >
                        Apply Payment
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Time Entry Modal for task completion */}
      {completingTask && (
        <TimeEntryModal
          task={completingTask}
          onSubmit={handleTimeEntrySubmit}
          onSkip={handleTimeEntrySkip}
          onCancel={handleTimeEntryCancel}
        />
      )}
    </Layout>
  );
}

interface DashboardCardProps {
  title: string;
  value: string;
  color?: string;
  link?: string;
  linkText?: string;
}

function DashboardCard({ title, value, color = '#1a237e', link, linkText }: DashboardCardProps) {
  return (
    <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
      <h3 style={{ color: '#666', fontSize: '14px', marginBottom: '8px' }}>{title}</h3>
      <p style={{ fontSize: '36px', fontWeight: 'bold', color, margin: '0 0 8px' }}>{value}</p>
      {link && <Link to={link} style={{ color: '#1a237e', fontSize: '14px' }}>{linkText} →</Link>}
    </div>
  );
}
